import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";

const TIER_CONFIG = {
  locked: {
    bg: "bg-red-50 border-red-200",
    icon: "🔒",
    badge: "bg-red-100 text-red-700",
    badgeText: "Access Suspended",
    message: (title) =>
      `Your access to "${title}" has been suspended — payment is overdue by 3+ days.`,
    cta: "Renew Now",
    ctaStyle: "bg-red-600 hover:bg-red-700 text-white",
  },
  partial_low: {
    bg: "bg-amber-50 border-amber-200",
    icon: "⚠️",
    badge: "bg-amber-100 text-amber-700",
    badgeText: "Partial Access",
    message: (title, pct) =>
      `You've paid ${pct}% for "${title}". Pay ≥60% to unlock assessments.`,
    cta: "Complete Payment",
    ctaStyle: "bg-amber-500 hover:bg-amber-600 text-white",
  },
  partial_high: {
    bg: "bg-blue-50 border-blue-200",
    icon: "📖",
    badge: "bg-blue-100 text-blue-700",
    badgeText: "Most Features Unlocked",
    message: (title, pct) =>
      `You've paid ${pct}% for "${title}". Pay 100% to unlock recordings and certificate.`,
    cta: "Complete Payment",
    ctaStyle: "bg-blue-600 hover:bg-blue-700 text-white",
  },
};

function Banner({ sub, onDismiss }) {
  const navigate = useNavigate();
  const cfg = TIER_CONFIG[sub.access_tier];
  if (!cfg) return null;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${cfg.bg} mb-3`}
    >
      <span className="text-xl flex-shrink-0 mt-0.5">{cfg.icon}</span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>
            {cfg.badgeText}
          </span>
          {sub.days_since_payment != null && sub.access_tier === "locked" && (
            <span className="text-xs text-red-500">
              {sub.days_since_payment} days since last payment
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700">
          {cfg.message(sub.course_title, sub.payment_pct)}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate("/cohorts")}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${cfg.ctaStyle}`}
        >
          {cfg.cta}
        </button>
        <button
          onClick={() => onDismiss(sub.subscriber_uuid)}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function PaymentStatusBanner() {
  const [subs, setSubs] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("dismissed_banners") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get("/subscribers/my-access-status")
      .then((res) => setSubs(res.data?.subscriptions || []))
      .catch(() => {});
  }, []);

  const handleDismiss = (uuid) => {
    const next = [...dismissed, uuid];
    setDismissed(next);
    sessionStorage.setItem("dismissed_banners", JSON.stringify(next));
  };

  const visible = subs.filter(
    (s) =>
      ["locked", "partial_low", "partial_high"].includes(s.access_tier) &&
      !dismissed.includes(s.subscriber_uuid)
  );

  if (visible.length === 0) return null;

  return (
    <div className="innersection pt-4">
      {visible.map((sub) => (
        <Banner key={sub.subscriber_uuid} sub={sub} onDismiss={handleDismiss} />
      ))}
    </div>
  );
}
